import { useState, useEffect, useCallback } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import Link from '@cloudscape-design/components/link';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import Alert from '@cloudscape-design/components/alert';
import { useNavigate } from 'react-router-dom';
import { getRecurringIssuesReport } from '../../api/tickets';
import { RecurringIssuesReport as RecurringData, RecurringTheme } from '../../types/ticket';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';

const WEEK_OPTIONS = [
  { id: '4', text: '4 weeks' },
  { id: '8', text: '8 weeks' },
  { id: '12', text: '12 weeks' },
];

function TrendBadge({ trend }: { trend: RecurringTheme['trend'] }) {
  if (trend === 'growing') return <StatusIndicator type="error">↑ Growing</StatusIndicator>;
  if (trend === 'shrinking') return <StatusIndicator type="success">↓ Shrinking</StatusIndicator>;
  return <StatusIndicator type="info">→ Stable</StatusIndicator>;
}

export function RecurringIssuesReport() {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState(8);
  const [report, setReport] = useState<RecurringData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReport = useCallback(async (w: number) => {
    setLoading(true);
    setError(null);
    try {
      setReport(await getRecurringIssuesReport(w));
    } catch (e: any) {
      setError(e.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(weeks);
  }, [weeks, fetchReport]);

  const handleDownload = () => {
    if (!report) return;
    const rows = report.themes.map((t) => [
      t.title, t.ticketCount, t.trend, t.ticketIds.join(';'),
    ]);
    downloadCsv(
      `recurring-issues-${weeks}w-${csvDateStamp()}.csv`,
      ['theme', 'ticketCount', 'trend', 'ticketIds'],
      rows,
    );
  };

  const handleThemeClick = (theme: RecurringTheme) => {
    const params = new URLSearchParams();
    params.set('ticketIds', theme.ticketIds.join(','));
    params.set('themeTitle', theme.title);
    navigate(`/tickets?${params.toString()}`);
  };

  const THEME_COLUMNS = [
    {
      id: 'title',
      header: 'Theme',
      cell: (item: RecurringTheme) => (
        <Link onFollow={(e) => { e.preventDefault(); handleThemeClick(item); }}>
          {item.title}
        </Link>
      ),
    },
    { id: 'count', header: 'Tickets', cell: (item: RecurringTheme) => item.ticketCount },
    { id: 'trend', header: 'Trend', cell: (item: RecurringTheme) => <TrendBadge trend={item.trend} /> },
    {
      id: 'sparkline',
      header: 'Weekly Activity',
      cell: (item: RecurringTheme) => (
        <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: '2px', height: '24px' }}>
          {item.weeklyBreakdown.map((w, i) => {
            const max = Math.max(...item.weeklyBreakdown.map((x) => x.count), 1);
            const height = Math.max(3, (w.count / max) * 24);
            return (
              <span
                key={i}
                title={`${w.weekStart}: ${w.count} tickets`}
                style={{
                  display: 'inline-block',
                  width: '8px',
                  height: `${height}px`,
                  backgroundColor: w.count > 0 ? '#0972d3' : '#e9ebed',
                  borderRadius: '1px',
                }}
              />
            );
          })}
        </span>
      ),
    },
  ];

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="AI-detected recurring patterns across tickets. Click a theme to view its tickets."
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <SegmentedControl
                  selectedId={String(weeks)}
                  options={WEEK_OPTIONS}
                  onChange={({ detail }) => setWeeks(parseInt(detail.selectedId))}
                />
                <Button iconName="download" onClick={handleDownload} disabled={!report}>
                  Download CSV
                </Button>
              </SpaceBetween>
            }
          >
            Recurring Issues
          </Header>
        }
      >
        {loading && <Box textAlign="center" padding="l"><Spinner size="large" /></Box>}
        {error && <Alert type="error">Failed to load report: {error}</Alert>}

        {report && !loading && (
          <SpaceBetween size="m">
            {report.concentration.totalTickets > 0 && report.themes.length > 0 && (
              <Alert type="info">
                Top 3 themes account for <strong>{report.concentration.top3Pct}%</strong> of
                all {report.concentration.totalTickets} tickets in the last {report.weeks} weeks
                {report.concentration.top3Pct >= 50 && ' — consider investing in root-cause fixes for these patterns'}
              </Alert>
            )}

            <Table
              items={report.themes}
              columnDefinitions={THEME_COLUMNS}
              variant="embedded"
              empty={
                <Box color="text-body-secondary" padding="l" textAlign="center">
                  No themes detected. Generate themes from the Dashboard first.
                </Box>
              }
              trackBy="title"
            />
          </SpaceBetween>
        )}
      </Container>

      {report && (
        <Box variant="small" color="text-body-secondary" textAlign="right">
          Generated: {new Date(report.generatedAt).toLocaleString()}
        </Box>
      )}
    </SpaceBetween>
  );
}
